import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../App";
import { toast } from "sonner";
import { Eye, EyeOff, Mail, Lock } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";

export default function LoginPage() {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const from = location.state?.from?.pathname || "/";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Please fill in all fields");
      return;
    }

    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back to All WHUFC!");
      navigate(from, { replace: true });
    } catch (error) {
      toast.error(error.response?.data?.detail || "Invalid credentials");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 flex" data-testid="login-page">
      {/* Left Panel - Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8">
        <div className="w-full max-w-md">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 mb-8">
            <div className="w-12 h-12 rounded-full bg-[#7C2C3B] flex items-center justify-center">
              <span className="text-white font-bold text-xl" style={{ fontFamily: 'Barlow Condensed' }}>WHF</span>
            </div>
            <span className="text-[#0F172A] font-bold text-2xl" style={{ fontFamily: 'Barlow Condensed' }}>WEST HAM FAMILY</span>
          </Link>

          <h1 className="text-4xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
            WELCOME BACK
          </h1>
          <p className="text-slate-600 mb-8">Sign in to access your account</p>

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <Label htmlFor="email" className="text-sm font-medium text-slate-700">Email Address</Label>
              <div className="relative mt-1">
                <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="you@example.com"
                  className="pl-10"
                  data-testid="login-email"
                />
              </div>
            </div>

            <div>
              <div className="flex items-center justify-between">
                <Label htmlFor="password" className="text-sm font-medium text-slate-700">Password</Label>
                <Link to="/forgot-password" className="text-xs text-[#7C2C3B] hover:underline">
                  Forgot password?
                </Link>
              </div>
              <div className="relative mt-1">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pl-10 pr-10"
                  data-testid="login-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
                </button>
              </div>
            </div>

            <Button 
              type="submit" 
              className="w-full bg-[#7C2C3B] hover:bg-[#63232F] text-white font-bold py-6"
              disabled={loading}
              data-testid="login-submit"
            >
              {loading ? "SIGNING IN..." : "SIGN IN"}
            </Button>
          </form>

          {/* Emergent Google Auth — DO NOT HARDCODE THE URL, OR ADD ANY
              FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH. */}
          <div className="mt-6">
            <div className="relative flex items-center my-4">
              <div className="flex-grow border-t border-slate-200" />
              <span className="mx-3 text-xs text-slate-400 uppercase tracking-wide">or</span>
              <div className="flex-grow border-t border-slate-200" />
            </div>
            <button
              type="button"
              data-testid="login-google-btn"
              onClick={() => {
                const redirect = `${window.location.origin}/auth-callback`;
                window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirect)}`;
              }}
              className="w-full flex items-center justify-center gap-2 border border-slate-300 hover:border-slate-400 bg-white hover:bg-slate-50 text-slate-700 font-medium py-3 rounded-md transition"
            >
              <svg width="18" height="18" viewBox="0 0 48 48" xmlns="http://www.w3.org/2000/svg">
                <path fill="#EA4335" d="M24 9.5c3.54 0 6.71 1.22 9.21 3.6l6.85-6.85C35.9 2.38 30.47 0 24 0 14.62 0 6.51 5.38 2.56 13.22l7.98 6.19C12.43 13.72 17.74 9.5 24 9.5z"/>
                <path fill="#4285F4" d="M46.98 24.55c0-1.57-.15-3.09-.38-4.55H24v9.02h12.94c-.58 2.96-2.26 5.48-4.78 7.18l7.73 6c4.51-4.18 7.09-10.36 7.09-17.65z"/>
                <path fill="#FBBC05" d="M10.53 28.59c-.48-1.45-.76-2.99-.76-4.59s.27-3.14.76-4.59l-7.98-6.19C.92 16.46 0 20.12 0 24c0 3.88.92 7.54 2.56 10.78l7.97-6.19z"/>
                <path fill="#34A853" d="M24 48c6.48 0 11.93-2.13 15.89-5.81l-7.73-6c-2.15 1.45-4.92 2.3-8.16 2.3-6.26 0-11.57-4.22-13.47-9.91l-7.98 6.19C6.51 42.62 14.62 48 24 48z"/>
              </svg>
              Continue with Google
            </button>
          </div>

          <p className="mt-8 text-center text-slate-600">
            Don't have an account?{" "}
            <Link to="/register" className="text-[#7C2C3B] font-semibold hover:underline" data-testid="login-register-link">
              Subscribe Now
            </Link>
          </p>
        </div>
      </div>

      {/* Right Panel - Image */}
      <div className="hidden lg:block lg:w-1/2 relative">
        <div className="absolute inset-0 bg-[#7C2C3B]">
          <img 
            src="https://images.unsplash.com/photo-1574629810360-7efbbe195018?auto=format&fit=crop&w=1200&q=80"
            alt="Football"
            className="w-full h-full object-cover opacity-30"
          />
        </div>
        <div className="absolute inset-0 flex items-center justify-center p-12">
          <div className="text-center">
            <h2 className="text-5xl font-bold text-white mb-4" style={{ fontFamily: 'Barlow Condensed' }}>
              FOREVER<br />BLOWING<br />BUBBLES
            </h2>
            <p className="text-white/80 text-lg max-w-md">
              Join thousands of Hammers fans in the ultimate West Ham community
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
